import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { RENTAL_PERIODS } from '@navicosa/shared-types';
import { useQuery } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errorText } from '../../api/client';
import {
  type Location,
  PERIOD_LABEL,
  type ProductListItem,
  type RequestDetail,
} from '../../api/types';

interface FormValues {
  full_name: string;
  phone: string;
  company_name?: string;
  email?: string;
  delivery_address: string;
  location_id?: string;
  rental: [Dayjs, Dayjs | null];
  delivery_at?: Dayjs;
  customer_note?: string;
  items: {
    product_id: string;
    quantity: number;
    rental_period: (typeof RENTAL_PERIODS)[number];
    customer_note?: string;
  }[];
}

/** Nhân viên nhập yêu cầu khách gọi điện/nhắn Zalo – cùng quy tắc kiểm tra và snapshot giá như Mini App. */
export function RequestCreatePage() {
  const nav = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const locs = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<Location[]>('/admin/locations'),
  });
  const products = useQuery({
    queryKey: ['products-pick'],
    queryFn: async () => (await api.page<ProductListItem>('/admin/products', { limit: 100 })).items,
  });

  const submit = async (v: FormValues) => {
    setSaving(true);
    try {
      const created = await api.post<RequestDetail>('/admin/rental-requests', {
        customer: {
          full_name: v.full_name,
          phone: v.phone,
          company_name: v.company_name || null,
          email: v.email || null,
        },
        delivery_address: v.delivery_address,
        location_id: v.location_id ?? null,
        delivery_at: v.delivery_at?.startOf('day').add(7, 'hour').toISOString() ?? null,
        rental_start_at: v.rental[0].startOf('day').add(7, 'hour').toISOString(),
        rental_end_at: v.rental[1]?.startOf('day').add(17, 'hour').toISOString() ?? null,
        customer_note: v.customer_note || null,
        items: v.items,
      });
      message.success(`Đã tạo ${created.request_code}`);
      nav(`/requests/${created.id}`, { replace: true });
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Nhập yêu cầu thuê
        </Typography.Title>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={submit}
        initialValues={{ items: [{ quantity: 1, rental_period: 'day' }] }}
        style={{ maxWidth: 960 }}
      >
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="full_name"
              label="Họ tên người liên hệ"
              rules={[{ required: true, min: 2 }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="phone" label="Số điện thoại" rules={[{ required: true }]}>
              <Input placeholder="0931 822 288" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="company_name" label="Công ty">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={16}>
            <Form.Item
              name="delivery_address"
              label="Địa điểm công trình"
              rules={[{ required: true, min: 5 }]}
            >
              <Input placeholder="KCN, nhà xưởng, địa chỉ" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="location_id" label="Tỉnh/thành">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={(locs.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="rental"
              label="Thời gian thuê dự kiến"
              rules={[{ required: true, message: 'Chọn ngày bắt đầu' }]}
            >
              <DatePicker.RangePicker
                format="DD/MM/YYYY"
                allowEmpty={[false, true]}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="delivery_at" label="Ngày giao xe">
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="email" label="Email">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Typography.Title level={5}>Thiết bị</Typography.Title>
        <Form.List name="items">
          {(fields, { add, remove }) => (
            <>
              {fields.map((f) => (
                <Space key={f.key} align="baseline" wrap>
                  <Form.Item
                    name={[f.name, 'product_id']}
                    rules={[{ required: true, message: 'Chọn thiết bị' }]}
                  >
                    <Select
                      style={{ width: 340 }}
                      showSearch
                      optionFilterProp="label"
                      placeholder="Thiết bị"
                      options={(products.data ?? []).map((p) => ({
                        value: p.id,
                        label: `${p.name}${p.is_demo ? ' (DEMO)' : ''}`,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item name={[f.name, 'quantity']} rules={[{ required: true }]}>
                    <InputNumber min={1} max={50} prefix="SL" style={{ width: 100 }} />
                  </Form.Item>
                  <Form.Item name={[f.name, 'rental_period']} rules={[{ required: true }]}>
                    <Select
                      style={{ width: 120 }}
                      options={RENTAL_PERIODS.map((p) => ({
                        value: p,
                        label: `Theo ${PERIOD_LABEL[p].toLowerCase()}`,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item name={[f.name, 'customer_note']}>
                    <Input placeholder="Ghi chú dòng" style={{ width: 220 }} />
                  </Form.Item>
                  {fields.length > 1 && (
                    <Button icon={<DeleteOutlined />} onClick={() => remove(f.name)} />
                  )}
                </Space>
              ))}
              <Button
                icon={<PlusOutlined />}
                onClick={() => add({ quantity: 1, rental_period: 'day' })}
                style={{ marginBottom: 16 }}
              >
                Thêm thiết bị
              </Button>
            </>
          )}
        </Form.List>
        <Form.Item name="customer_note" label="Yêu cầu kỹ thuật / điều kiện mặt bằng / ghi chú">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={saving}>
          Tạo yêu cầu
        </Button>
      </Form>
    </Card>
  );
}
